// Left-column hero — dark editorial backdrop. Per user request, all
// text overlays are stripped (was: top-left "Plate · 01" amber kicker,
// bottom-right machine name label, huge faded family wordmark behind).
// Just the photo on a charcoal field with a soft amber vignette now.

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { useConfigurator } from "../store";
import { assetUrl, displayNameFor, imageCandidatesFor } from "../format";

export function Hero() {
  const { state } = useConfigurator();
  const catalog = state.catalog;
  const selected =
    catalog && state.selectedMachineId
      ? catalog.byId.get(state.selectedMachineId)
      : null;

  let candidates: string[] = [];
  let alt = "Build a Machine";
  if (selected) {
    alt = displayNameFor(selected);
    candidates = imageCandidatesFor(selected);
    if (selected.category === "machine") {
      // Family-level fallback when no per-product image exists.
      // Same extension priority as the per-product loader in format.ts.
      candidates.push(assetUrl(`images/products/${selected.family}.webp`));
      candidates.push(assetUrl(`images/products/${selected.family}.jpg`));
      candidates.push(assetUrl(`images/products/${selected.family}.png`));
      candidates.push(assetUrl(`images/products/${selected.family}.gif`));
    }
  } else {
    // Splash hero shown before the customer picks a machine. An
    // animated WebP loop of the Gammill setup, swapped in via the
    // standard extension walk so future format upgrades are a file
    // drop-in (build-a-machine.jpg / .png also fall back if needed).
    candidates.push(assetUrl(`images/build-a-machine.webp`));
    candidates.push(assetUrl(`images/build-a-machine.jpg`));
    candidates.push(assetUrl(`images/build-a-machine.png`));
  }

  // Fit policy.
  //   • Product photos → `object-contain` at EVERY breakpoint, so the whole
  //     machine is always fully in frame and the mobile→desktop transition
  //     doesn't suddenly crop. (Previously products used `lg:object-cover`,
  //     which filled the desktop card but cropped the top/bottom unpredictably
  //     as the column's aspect changed with window size.) The white card gives
  //     the cutout a clean gallery frame; the trade-off is more whitespace for
  //     photos with lots of baked-in margin — fix those by tightening the
  //     source crop, not by cropping in CSS.
  //   • Splash scene → `object-cover` full-bleed at every width (a wide
  //     photographic scene with no single subject to clip; contain would just
  //     leave large empty bars).
  // Full literal class strings (not interpolated) so Tailwind's JIT keeps them.
  const isSplash = !selected;
  const fitClass = isSplash ? "object-cover" : "object-contain";

  return (
    <div className="relative h-full w-full overflow-hidden bg-ink">
      {/* Soft amber radial highlight from the lower-right to add depth. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 90% at 75% 60%, rgba(250,173,61,0.10) 0%, rgba(17,17,17,0) 55%)",
        }}
      />

      {/* Photo overlay — covers when loaded, hides on 404. */}
      <AnimatePresence mode="wait">
        <HeroImage
          key={candidates.join("|")}
          candidates={candidates}
          alt={alt}
          fitClass={fitClass}
        />
      </AnimatePresence>
    </div>
  );
}

function HeroImage({
  candidates,
  alt,
  fitClass,
}: {
  candidates: string[];
  alt: string;
  fitClass: string;
}) {
  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [candidates.join("|")]);

  if (candidates.length === 0 || idx >= candidates.length) return null;

  // Gallery-card treatment — the product photos are catalog cutouts on
  // white backgrounds, so we frame each one inside a soft white card with
  // a deep drop shadow sitting on the dark hero. Reads like a museum-print
  // / gallery frame instead of a sticker pasted onto the backdrop.
  return (
    <motion.div
      key={candidates[idx]}
      initial={{ opacity: 0, scale: 0.96, y: 6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      // Outer padding bumps the white card off the hero edges. Tightened
      // from p-10/p-14/p-20 → p-3/p-4/p-6 so the card dominates the area
      // and leaves the requested 15-18% max margin per side.
      className="absolute inset-0 flex items-center justify-center p-3 sm:p-4 lg:p-6"
    >
      <div className="relative flex h-full max-h-[94%] w-full max-w-[94%] items-center justify-center overflow-hidden rounded-card bg-white shadow-[0_30px_70px_-15px_rgba(0,0,0,0.55),0_8px_25px_-8px_rgba(0,0,0,0.35)]">
        {/* Fit class is `object-cover` by default (crops baked-in
            whitespace) or `object-contain` for tightly-framed source
            images that would lose detail if cropped. See Hero() for
            the per-image fit policy. */}
        <img
          src={candidates[idx]}
          alt={alt}
          onError={() => setIdx((i) => i + 1)}
          className={`h-full w-full ${fitClass}`}
        />
      </div>
    </motion.div>
  );
}
